'use client';

import { useEffect, useRef, useState } from 'react';
import { useLoadedWebpSource } from '@/components/tools/webp-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { createZipBlob } from '@/lib/browser-png-tools';
import { decodeAnimatedWebpFrames, formatFileSize, getBaseName } from '@/lib/browser-webp-tools';

type ExtractedFrame = {
  id: string;
  index: number;
  name: string;
  blob: Blob;
  previewUrl: string;
  size: number;
  delayMs: number;
};

export default function BrowserWebpFrameExtractor() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource } = useLoadedWebpSource();
  const [frames, setFrames] = useState<ExtractedFrame[]>([]);
  const [selectedFrameId, setSelectedFrameId] = useState<string | null>(null);
  const [zipUrl, setZipUrl] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      frames.forEach((frame) => URL.revokeObjectURL(frame.previewUrl));

      if (zipUrl) {
        URL.revokeObjectURL(zipUrl);
      }
    };
  }, [frames, zipUrl]);

  function resetOutputState() {
    frames.forEach((frame) => URL.revokeObjectURL(frame.previewUrl));

    if (zipUrl) {
      URL.revokeObjectURL(zipUrl);
    }

    setFrames([]);
    setSelectedFrameId(null);
    setZipUrl(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The WebP could not be opened.');
    }
  }

  async function runExtraction() {
    if (!source) {
      setErrorMessage('Choose one WebP first.');
      return;
    }

    if (!source.metadata.animated) {
      setErrorMessage('This WebP is static. Use WebP Palette Extractor or WebP Optimizer instead.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'webp-frame-extractor',
        'Credits are not available for another WebP frame extraction right now.',
      );
      const decodedFrames = await decodeAnimatedWebpFrames(source.file);
      const nextFrames = decodedFrames.map((frame) => ({
        id: `webp-frame-${frame.index}`,
        index: frame.index,
        name: frame.name,
        blob: frame.blob,
        previewUrl: URL.createObjectURL(frame.blob),
        size: frame.blob.size,
        delayMs: frame.delayMs,
      }));
      const zipBlob = await createZipBlob(
        nextFrames.map((frame) => ({
          name: frame.name,
          data: frame.blob,
        })),
      );

      setFrames(nextFrames);
      setSelectedFrameId(nextFrames[0]?.id ?? null);
      setZipUrl(URL.createObjectURL(zipBlob));
      setCreditMessage(formatToolCreditSuccess('WebP frame extraction', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The WebP frames could not be extracted.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const selectedFrame = frames.find((frame) => frame.id === selectedFrameId) ?? null;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Extract animated WebP frames as PNG files</h2>
          <p>
            Decode one animated WebP locally, inspect every frame, download a selected PNG, or
            export the full frame sequence as a ZIP.
          </p>
        </div>
        <span className="tool-live-badge">Frames</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source ? source.file.name : 'Choose one animated WebP or drop it here'}
              </strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Animated WebP only'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/webp,.webp"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runExtraction()}
              type="button"
            >
              {isProcessing ? 'Extracting…' : 'Extract frames'}
            </button>
            {selectedFrame ? (
              <a
                className="button button-ghost"
                download={selectedFrame.name}
                href={selectedFrame.previewUrl}
              >
                Download selected PNG
              </a>
            ) : null}
            {zipUrl && source ? (
              <a
                className="button button-ghost"
                download={`${getBaseName(source.file.name)}-frames.zip`}
                href={zipUrl}
              >
                Download ZIP
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Frame extraction</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Frames</strong>
              <span>{frames.length || source?.metadata.frameCount || 'Not extracted yet'}</span>
            </div>
            <div>
              <strong>Duration</strong>
              <span>
                {source?.metadata.animated
                  ? `${(source.metadata.durationMs / 1000).toFixed(2)}s`
                  : 'Static'}
              </span>
            </div>
            <div>
              <strong>Loop</strong>
              <span>
                {source?.metadata.loopCount === 0
                  ? 'Infinite'
                  : (source?.metadata.loopCount ?? 'Unknown')}
              </span>
            </div>
            {selectedFrame ? (
              <div>
                <strong>Selected</strong>
                <span>{`Frame ${selectedFrame.index}`}</span>
              </div>
            ) : null}
          </div>
        </aside>
      </div>

      {frames.length ? (
        <div className="tool-live-preview">
          <h3>Frames</h3>
          <div className="tool-apng-frame-list">
            {frames.map((frame) => (
              <button
                key={frame.id}
                className={`tool-apng-frame-card ${frame.id === selectedFrameId ? 'tool-gif-frame-card-active' : ''}`}
                onClick={() => setSelectedFrameId(frame.id)}
                type="button"
              >
                <div className="tool-apng-frame-thumb">
                  <img alt={`Frame ${frame.index} preview`} src={frame.previewUrl} />
                </div>
                <div className="tool-apng-frame-copy">
                  <strong>{`Frame ${frame.index}`}</strong>
                  <span>{`${formatFileSize(frame.size)} • ${frame.delayMs} ms`}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}
